import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import { TagOption } from 'components/shared/Tags/model';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { MultiValueContainer } from 'components/shared/underlyings/selector/multi/multi-value-container';
import { MenuList } from 'components/shared/underlyings/selector/result/menu-list';
import { NoOptionsMessage } from 'components/shared/underlyings/selector/result/no-option-message';
import { Option } from 'components/shared/underlyings/selector/result/option';
import { useTags } from 'hooks/tags/use-tags';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { useCallback, useEffect } from 'react';
import { basketOptimizerUnderlyingTagsChanged } from 'state/advisory/basket-optimizer/actions';
import { UnderlyingTag } from 'state/advisory/basket-optimizer/model';
import { getIsMutuallyExclusive, getUnderlyingTags } from 'state/advisory/basket-optimizer/selectors';
import { getQuoteUnderlyings } from 'state/selectors/common/commonForm';
import { getUserPreferencesUnderlyingCodeType } from 'state/userPreferences/userPreferences.selectors';
import { getUnderlyingCode } from 'utils/underlying/underlying-code';

const toUnderlyingTagList = (
  underlyings: Underlying[] = [],
  defaultUnderlyingCodeType: UnderlyingCodeType,
): UnderlyingTag[] => underlyings.map(underlying => toUnderlyingTag(underlying, defaultUnderlyingCodeType));

const toUnderlyingTag = (underlying: Underlying, defaultUnderlyingCodeType: UnderlyingCodeType): UnderlyingTag => {
  const underlyingCode = getUnderlyingCode(underlying, defaultUnderlyingCodeType);
  return {
    label: underlyingCode,
    value: underlying.id,
    rawValue: underlying,
    isHighlighted: false,
    isDisabled: true,
  };
};

const useUnderlyings = (tabId: string) => {
  const dispatch = useThunkDispatch();
  const defaultUnderlyingCodeType = useAppSelector(getUserPreferencesUnderlyingCodeType);
  const isMutuallyExclusive = useAppSelector(s => getIsMutuallyExclusive(s, tabId));
  const underlyingTags = useAppSelector(s => getUnderlyingTags(s, tabId) ?? []);
  const underlyings = useAppSelector(s => getQuoteUnderlyings(s, tabId))!;

  const onUnderlyingsTagsChanged = useCallback(
    (tags: ReadonlyArray<TagOption<Underlying>>) => {
      dispatch(basketOptimizerUnderlyingTagsChanged(tabId, tags as UnderlyingTag[]));
    },
    [dispatch, tabId],
  );

  useEffect(() => {
    if (underlyingTags.length < underlyings.length) {
      onUnderlyingsTagsChanged(toUnderlyingTagList(underlyings, defaultUnderlyingCodeType));
    }
  }, [underlyingTags, defaultUnderlyingCodeType, underlyings, onUnderlyingsTagsChanged]);

  return {
    defaultUnderlyingCodeType,
    isMutuallyExclusive,
    underlyingTags,
    onUnderlyingsTagsChanged,
  };
};
type Props = {
  tabId: string;
  fieldId: string;
};

export function UnderlyingsSelector({ tabId, fieldId }: Props) {
  const { defaultUnderlyingCodeType, isMutuallyExclusive, underlyingTags, onUnderlyingsTagsChanged } =
    useUnderlyings(tabId);

  const { search, getOptionsFromPastedLabels } = useUnderlyingSearchByFormId(tabId, fieldId);

  const { onTagHighlightToggled, onTagRemoved } = useTags(underlyingTags, onUnderlyingsTagsChanged);

  return (
    <TagsPicker<Underlying>
      className="h-auto"
      components={{
        MenuList,
        Option,
        NoOptionsMessage,
        ClearIndicator: ClearIndicator.Default,
        MultiValueContainer: p => (
          <MultiValueContainer
            onTagHighlightToggled={onTagHighlightToggled}
            onTagRemoved={onTagRemoved}
            canHighlight={!isMutuallyExclusive}
            {...p}
          />
        ),
      }}
      isMulti={true}
      convertPastedLabelsToTags={inputs => getOptionsFromPastedLabels(inputs, [], defaultUnderlyingCodeType)}
      filterTags={input => search(input, [], [], defaultUnderlyingCodeType)}
      id="basket-optimizer-underlying-tags"
      isClearable={false}
      isMenuListNarrow
      onTagsChanged={onUnderlyingsTagsChanged}
      placeholder="Search or paste underlyings"
      tags={underlyingTags}
    />
  );
}
